import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/Input';
import LocationPicker from '../components/LocationPicker';
import { useAuth } from '../context/AuthContext';
import {
  createEvent,
  deleteEvent,
  emailPrivateEventQR,
  getEvents,
  getXperiences,
  uploadEventPoster,
  updateEvent,
  Xperience,
  XperienceEvent
} from '../services/api';
import { t } from '../i18n';
import './EventsPage.css';

const XP_SEARCH_LIMIT = 80;
const EVENT_POSTER_WIDTH = 1920;
const EVENT_POSTER_HEIGHT = 420;
const MIN_POSTER_ZOOM = 1;
const MAX_POSTER_ZOOM = 3;

const emptyForm = {
  title: '',
  description: '',
  eventType: 'wedding',
  locationName: '',
  locationLongitude: '',
  locationLatitude: '',
  eventDate: '',
  coverUrl: '',
  isPublic: false,
  password: '',
  xperienceIds: [] as string[]
};

type PosterCropState = {
  file: File;
  objectUrl: string;
  naturalWidth: number;
  naturalHeight: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

type PosterCropViewport = {
  width: number;
  height: number;
};

type PosterCropDragState = {
  startX: number;
  startY: number;
  initialOffsetX: number;
  initialOffsetY: number;
};

const emptyPosterCropViewport: PosterCropViewport = { width: 0, height: 0 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const extractPointCoordinates = (
  point?: { type?: string; coordinates?: [number, number] } | null,
  fallbackLongitude?: number | string | null,
  fallbackLatitude?: number | string | null
) => {
  if (point && Array.isArray(point.coordinates) && point.coordinates.length === 2) {
    return {
      longitude: String(point.coordinates[0]),
      latitude: String(point.coordinates[1])
    };
  }

  return {
    longitude: fallbackLongitude !== undefined && fallbackLongitude !== null ? String(fallbackLongitude) : '',
    latitude: fallbackLatitude !== undefined && fallbackLatitude !== null ? String(fallbackLatitude) : ''
  };
};

const assetUrl = (rawUrl?: string | null) => {
  const value = rawUrl?.trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `https://x-perienc.es/${value.replace(/^\/+/, '')}`;
};

const posterUrlForXperience = (xperience: Xperience) => {
  const posterUrl = xperience.mediaLinks?.find((media) => media.posterUrl)?.posterUrl;
  return assetUrl(posterUrl);
};

const posterCropMetrics = (crop: PosterCropState, viewport: PosterCropViewport) => {
  if (viewport.width <= 0 || viewport.height <= 0) return null;

  const scale = Math.max(viewport.width / crop.naturalWidth, viewport.height / crop.naturalHeight) * crop.zoom;
  const renderedWidth = crop.naturalWidth * scale;
  const renderedHeight = crop.naturalHeight * scale;
  const maxOffsetX = Math.max(0, (renderedWidth - viewport.width) / 2);
  const maxOffsetY = Math.max(0, (renderedHeight - viewport.height) / 2);
  const offsetX = clamp(crop.offsetX, -maxOffsetX, maxOffsetX);
  const offsetY = clamp(crop.offsetY, -maxOffsetY, maxOffsetY);

  return {
    scale,
    renderedWidth,
    renderedHeight,
    offsetX,
    offsetY,
    left: (viewport.width - renderedWidth) / 2 + offsetX,
    top: (viewport.height - renderedHeight) / 2 + offsetY
  };
};

const clampPosterCrop = (crop: PosterCropState, viewport: PosterCropViewport) => {
  const metrics = posterCropMetrics(crop, viewport);
  if (!metrics) return crop;

  return {
    ...crop,
    offsetX: metrics.offsetX,
    offsetY: metrics.offsetY
  };
};

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const createCroppedPosterFile = async (crop: PosterCropState, viewport: PosterCropViewport) => {
  const metrics = posterCropMetrics(crop, viewport);
  if (!metrics) {
    throw new Error('Poster crop viewport is not ready');
  }

  const image = await loadImage(crop.objectUrl);
  const sourceX = clamp(-metrics.left / metrics.scale, 0, crop.naturalWidth);
  const sourceY = clamp(-metrics.top / metrics.scale, 0, crop.naturalHeight);
  const sourceWidth = clamp(viewport.width / metrics.scale, 1, crop.naturalWidth - sourceX);
  const sourceHeight = clamp(viewport.height / metrics.scale, 1, crop.naturalHeight - sourceY);
  const canvas = document.createElement('canvas');
  canvas.width = EVENT_POSTER_WIDTH;
  canvas.height = EVENT_POSTER_HEIGHT;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas context is not available');
  }

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    EVENT_POSTER_WIDTH,
    EVENT_POSTER_HEIGHT
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((createdBlob) => {
      if (createdBlob) {
        resolve(createdBlob);
      } else {
        reject(new Error('Failed to create poster blob'));
      }
    }, 'image/webp', 0.9);
  });

  const baseName = crop.file.name.replace(/\.[^.]+$/, '') || 'event-poster';
  return new File([blob], `${baseName}-event-poster.webp`, { type: blob.type || 'image/webp' });
};

const EventsPage: React.FC = () => {
  const { creator, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams<{ eventId?: string }>();
  const isEditor = location.pathname === '/events/new' || Boolean(eventId);
  const [events, setEvents] = useState<XperienceEvent[]>([]);
  const [xperiences, setXperiences] = useState<Xperience[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [xperiencesLoading, setXperiencesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadProgress, setCoverUploadProgress] = useState(0);
  const [temporaryUploadedCoverUrl, setTemporaryUploadedCoverUrl] = useState<string | null>(null);
  const [posterCrop, setPosterCrop] = useState<PosterCropState | null>(null);
  const [posterCropViewport, setPosterCropViewport] = useState<PosterCropViewport>(emptyPosterCropViewport);
  const [posterCropDragging, setPosterCropDragging] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [xperienceSearch, setXperienceSearch] = useState('');
  const [draggingXperienceId, setDraggingXperienceId] = useState<string | null>(null);
  const [dragOverXperienceId, setDragOverXperienceId] = useState<string | null>(null);
  const coverFileInputRef = useRef<HTMLInputElement>(null);
  const posterCropViewportRef = useRef<HTMLDivElement>(null);
  const posterCropDragRef = useRef<PosterCropDragState | null>(null);
  const suppressXperienceClickRef = useRef(false);

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === eventId) || null,
    [events, eventId]
  );
  const posterCropObjectUrl = posterCrop?.objectUrl;

  const selectedXperiences = useMemo(() => {
    const byId = new Map<string, Xperience>();
    for (const eventXperience of selectedEvent?.xperiences || []) {
      byId.set(eventXperience.id, eventXperience);
    }
    for (const xperience of xperiences) {
      byId.set(xperience.id, xperience);
    }

    return form.xperienceIds
      .map((id) => byId.get(id))
      .filter(Boolean) as Xperience[];
  }, [form.xperienceIds, selectedEvent?.xperiences, xperiences]);

  useEffect(() => {
    if (!creator) {
      navigate('/login');
      return;
    }

    if (!isProfileComplete) {
      navigate('/profile', { state: { message: t('home.profileIncomplete') } });
      return;
    }

    loadEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator, isProfileComplete, navigate]);

  useEffect(() => {
    if (!isEditor) return;
    loadXperienceOptions('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor]);

  useEffect(() => {
    if (!isEditor) return;

    const searchTimer = window.setTimeout(() => {
      loadXperienceOptions(xperienceSearch);
    }, 250);

    return () => window.clearTimeout(searchTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xperienceSearch, isEditor]);

  useEffect(() => {
    if (!isEditor || location.pathname === '/events/new') {
      setForm(emptyForm);
      setTemporaryUploadedCoverUrl(null);
      setPosterCrop(null);
      return;
    }

    if (!selectedEvent) return;

    const point = extractPointCoordinates(selectedEvent.location, selectedEvent.longitude, selectedEvent.latitude);
    setForm({
      title: selectedEvent.title || '',
      description: selectedEvent.description || '',
      eventType: selectedEvent.eventType || 'private_event',
      locationName: selectedEvent.locationName || '',
      locationLongitude: point.longitude,
      locationLatitude: point.latitude,
      eventDate: selectedEvent.eventDate || '',
      coverUrl: selectedEvent.coverUrl || '',
      isPublic: selectedEvent.isPublic === 1,
      password: selectedEvent.password || '',
      xperienceIds: selectedEvent.xperienceIds || []
    });
    setTemporaryUploadedCoverUrl(null);
  }, [isEditor, location.pathname, selectedEvent]);

  useEffect(() => {
    const objectUrl = posterCropObjectUrl;
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [posterCropObjectUrl]);

  useEffect(() => {
    if (!posterCropObjectUrl) return;

    const updateViewport = () => {
      const rect = posterCropViewportRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewport = { width: rect.width, height: rect.height };
      setPosterCropViewport(viewport);
      setPosterCrop((current) => current ? clampPosterCrop(current, viewport) : current);
    };

    window.setTimeout(updateViewport, 0);
    window.addEventListener('resize', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, [posterCropObjectUrl]);

  const loadEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const eventsResponse = await getEvents();
      setEvents(eventsResponse.data.events);
    } catch {
      setError(t('events.error.load'));
    } finally {
      setLoading(false);
    }
  };

  const loadXperienceOptions = async (search: string) => {
    setXperiencesLoading(true);
    try {
      const response = await getXperiences({
        page: 1,
        limit: XP_SEARCH_LIMIT,
        scope: creator?.role === 'administrator' ? 'all' : 'mine',
        ...(search.trim() ? { search: search.trim() } : {})
      });
      setXperiences(response.data.xperiences);
    } catch {
      setError(t('events.error.loadXperiences'));
    } finally {
      setXperiencesLoading(false);
    }
  };

  const handleToggleXperience = (id: string) => {
    setForm((prev) => {
      const exists = prev.xperienceIds.includes(id);
      return {
        ...prev,
        xperienceIds: exists
          ? prev.xperienceIds.filter((candidate) => candidate !== id)
          : [...prev.xperienceIds, id]
      };
    });
  };

  const moveSelectedXperience = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;

    setForm((prev) => {
      const ids = [...prev.xperienceIds];
      const sourceIndex = ids.indexOf(sourceId);
      const targetIndex = ids.indexOf(targetId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return prev;
      }

      const [movedId] = ids.splice(sourceIndex, 1);
      ids.splice(targetIndex, 0, movedId);

      return {
        ...prev,
        xperienceIds: ids
      };
    });
  };

  const clearSelectedXperienceDragState = () => {
    setDraggingXperienceId(null);
    setDragOverXperienceId(null);
  };

  const draggedXperienceIdFromEvent = (event: React.DragEvent) =>
    draggingXperienceId ||
    event.dataTransfer.getData('application/x-xperience-id') ||
    event.dataTransfer.getData('text/plain');

  const handleSelectedXperienceDragStart = (event: React.DragEvent, xperienceId: string) => {
    setDraggingXperienceId(xperienceId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/x-xperience-id', xperienceId);
    event.dataTransfer.setData('text/plain', xperienceId);
  };

  const handleSelectedXperienceDragOver = (event: React.DragEvent, targetId: string) => {
    const sourceId = draggedXperienceIdFromEvent(event);
    if (!sourceId || sourceId === targetId) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverXperienceId(targetId);
  };

  const handleSelectedXperienceDrop = (event: React.DragEvent, targetId: string) => {
    event.preventDefault();
    const sourceId = draggedXperienceIdFromEvent(event);
    if (sourceId) {
      moveSelectedXperience(sourceId, targetId);
    }

    suppressXperienceClickRef.current = true;
    clearSelectedXperienceDragState();
    window.setTimeout(() => {
      suppressXperienceClickRef.current = false;
    }, 150);
  };

  const uploadPosterFile = async (file: File) => {
    setCoverUploading(true);
    setCoverUploadProgress(0);
    setError('');

    try {
      const response = await uploadEventPoster(file, temporaryUploadedCoverUrl, (progress) => {
        setCoverUploadProgress(progress);
      });
      setForm((prev) => ({
        ...prev,
        coverUrl: response.data.coverUrl
      }));
      setTemporaryUploadedCoverUrl(response.data.coverUrl);
    } catch {
      setError(t('events.error.coverUpload'));
    } finally {
      setCoverUploading(false);
      setCoverUploadProgress(0);
    }
  };

  const handleEventPosterFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (coverFileInputRef.current) {
      coverFileInputRef.current.value = '';
    }
    if (!file) return;

    setError('');

    if (!file.type.startsWith('image/')) {
      setError(t('events.error.posterRead'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      setPosterCropViewport(emptyPosterCropViewport);
      setPosterCrop({
        file,
        objectUrl,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
        zoom: MIN_POSTER_ZOOM,
        offsetX: 0,
        offsetY: 0
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setError(t('events.error.posterRead'));
    };

    image.src = objectUrl;
  };

  const closePosterCrop = () => {
    posterCropDragRef.current = null;
    setPosterCropDragging(false);
    setPosterCrop(null);
  };

  const handlePosterCropZoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const zoom = clamp(Number(event.target.value), MIN_POSTER_ZOOM, MAX_POSTER_ZOOM);
    setPosterCrop((current) => current
      ? clampPosterCrop({ ...current, zoom }, posterCropViewport)
      : current
    );
  };

  const handlePosterCropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!posterCrop) return;

    const metrics = posterCropMetrics(posterCrop, posterCropViewport);
    posterCropDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialOffsetX: metrics?.offsetX ?? posterCrop.offsetX,
      initialOffsetY: metrics?.offsetY ?? posterCrop.offsetY
    };
    setPosterCropDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePosterCropPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!posterCropDragRef.current) return;

    const dragState = posterCropDragRef.current;
    setPosterCrop((current) => {
      if (!current) return current;

      return clampPosterCrop({
        ...current,
        offsetX: dragState.initialOffsetX + event.clientX - dragState.startX,
        offsetY: dragState.initialOffsetY + event.clientY - dragState.startY
      }, posterCropViewport);
    });
  };

  const handlePosterCropPointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    posterCropDragRef.current = null;
    setPosterCropDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleConfirmPosterCrop = async () => {
    if (!posterCrop) return;

    try {
      const croppedFile = await createCroppedPosterFile(posterCrop, posterCropViewport);
      closePosterCrop();
      await uploadPosterFile(croppedFile);
    } catch {
      setError(t('events.error.posterCrop'));
      if (coverFileInputRef.current) {
        coverFileInputRef.current.value = '';
      }
    }
  };

  const renderPosterCropModal = () => {
    if (!posterCrop) return null;

    const metrics = posterCropMetrics(posterCrop, posterCropViewport);
    const imageStyle: React.CSSProperties = metrics ? {
      width: `${metrics.renderedWidth}px`,
      height: `${metrics.renderedHeight}px`,
      left: `${metrics.left}px`,
      top: `${metrics.top}px`
    } : {};

    return (
      <div className="event-poster-crop-overlay" role="dialog" aria-modal="true" aria-labelledby="event-poster-crop-title">
        <div className="event-poster-crop-dialog">
          <div className="event-poster-crop-header">
            <h2 id="event-poster-crop-title">{t('events.posterCropTitle')}</h2>
            <button type="button" className="event-poster-crop-close" onClick={closePosterCrop} aria-label={t('events.cancel')}>
              x
            </button>
          </div>

          <div
            ref={posterCropViewportRef}
            className={`event-poster-crop-frame ${posterCropDragging ? 'event-poster-crop-frame-dragging' : ''}`}
            onPointerDown={handlePosterCropPointerDown}
            onPointerMove={handlePosterCropPointerMove}
            onPointerUp={handlePosterCropPointerEnd}
            onPointerCancel={handlePosterCropPointerEnd}
          >
            <img
              src={posterCrop.objectUrl}
              alt=""
              className="event-poster-crop-image"
              draggable={false}
              style={imageStyle}
            />
            <span className="event-poster-crop-mask" aria-hidden="true" />
          </div>

          <div className="event-poster-crop-controls">
            <label htmlFor="event-poster-crop-zoom">{t('events.posterCropZoom')}</label>
            <input
              id="event-poster-crop-zoom"
              type="range"
              min={MIN_POSTER_ZOOM}
              max={MAX_POSTER_ZOOM}
              step={0.01}
              value={posterCrop.zoom}
              onChange={handlePosterCropZoomChange}
            />
          </div>

          <div className="event-poster-crop-actions">
            <Button variant="ghost" type="button" onClick={closePosterCrop}>
              {t('events.cancel')}
            </Button>
            <Button variant="secondary" type="button" onClick={handleConfirmPosterCrop} disabled={coverUploading}>
              {t('events.posterCropUse')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (coverUploading) return;
    setSaving(true);
    setError('');

    const hasLocation = form.locationLongitude.trim() !== '' && form.locationLatitude.trim() !== '';
    const payload = {
      title: form.title,
      description: form.description,
      eventType: form.eventType,
      locationName: form.locationName.trim(),
      longitude: hasLocation ? Number(form.locationLongitude) : null,
      latitude: hasLocation ? Number(form.locationLatitude) : null,
      eventDate: form.eventDate || null,
      coverUrl: form.coverUrl,
      isPublic: form.isPublic ? 1 : 0,
      password: form.isPublic ? null : form.password,
      xperienceIds: form.xperienceIds
    };

    try {
      if (eventId) {
        await updateEvent(eventId, payload);
      } else {
        await createEvent(payload);
      }
      await loadEvents();
      navigate('/events');
    } catch {
      setError(t('events.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (targetEventId: string) => {
    if (!window.confirm(t('events.confirmDelete'))) return;
    setSaving(true);
    setError('');
    try {
      await deleteEvent(targetEventId);
      await loadEvents();
      if (eventId === targetEventId) navigate('/events');
    } catch {
      setError(t('events.error.delete'));
    } finally {
      setSaving(false);
    }
  };

  const showTemporarySuccess = (message: string) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3000);
  };

  const handleCopyEventPassword = (password: string) => {
    navigator.clipboard.writeText(password);
    showTemporarySuccess(t('events.success.passwordCopied'));
  };

  const handleSendEventEmailQR = async () => {
    if (!eventId || emailSending) return;

    setError('');
    setEmailSending(true);
    try {
      await emailPrivateEventQR(eventId);
      showTemporarySuccess(t('events.success.emailSent'));
    } catch {
      setError(t('events.error.emailQR'));
    } finally {
      setEmailSending(false);
    }
  };

  const renderXperienceCard = (xperience: Xperience, options: { sortable?: boolean; index?: number } = {}) => {
    const isSelected = form.xperienceIds.includes(xperience.id);
    const posterUrl = posterUrlForXperience(xperience);
    const isSortable = Boolean(options.sortable && isSelected);
    const isDragging = draggingXperienceId === xperience.id;
    const isDropTarget = dragOverXperienceId === xperience.id && draggingXperienceId !== xperience.id;

    return (
      <button
        type="button"
        key={xperience.id}
        className={[
          'event-xperience-card',
          isSelected ? 'event-xperience-card-selected' : '',
          isSortable ? 'event-xperience-card-sortable' : '',
          isDragging ? 'event-xperience-card-dragging' : '',
          isDropTarget ? 'event-xperience-card-drop-target' : ''
        ].filter(Boolean).join(' ')}
        draggable={isSortable}
        title={isSortable ? t('events.reorderXperience') : undefined}
        onClick={() => {
          if (suppressXperienceClickRef.current) return;
          handleToggleXperience(xperience.id);
        }}
        onDragStart={isSortable ? (event) => handleSelectedXperienceDragStart(event, xperience.id) : undefined}
        onDragOver={isSortable ? (event) => handleSelectedXperienceDragOver(event, xperience.id) : undefined}
        onDrop={isSortable ? (event) => handleSelectedXperienceDrop(event, xperience.id) : undefined}
        onDragEnd={isSortable ? clearSelectedXperienceDragState : undefined}
      >
        {isSortable && (
          <span className="event-xperience-order">{(options.index ?? 0) + 1}</span>
        )}
        <div className="event-xperience-poster">
          {posterUrl ? (
            <img src={posterUrl} alt="" />
          ) : (
            <span>-</span>
          )}
        </div>
        <div className="event-xperience-info">
          <strong>{xperience.title}</strong>
          <span>
            {(xperience.mediaLinks?.length || 0)} {t('events.mediaItems')}
          </span>
        </div>
        <span className="event-xperience-check">{isSelected ? 'OK' : '+'}</span>
        {isSortable && (
          <span className="event-xperience-drag-handle" aria-hidden="true" />
        )}
      </button>
    );
  };

  const renderList = () => (
    <>
      <div className="events-header">
        <div>
          <h1 className="events-title">{t('events.title')}</h1>
          <p className="events-subtitle">{t('events.subtitle')}</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/events/new')}>
          {t('events.newEvent')}
        </Button>
      </div>

      {loading ? (
        <div className="events-loading">{t('events.loading')}</div>
      ) : (
        <section className="events-list events-list-full">
          {events.length === 0 ? (
            <div className="events-empty">
              <h2>{t('events.emptyTitle')}</h2>
              <p>{t('events.emptyText')}</p>
            </div>
          ) : (
            events.map((event) => (
              <article key={event.id} className="event-card">
                <div className="event-card-cover" style={{ backgroundImage: event.coverUrl ? `url(${assetUrl(event.coverUrl)})` : undefined }}>
                  {!event.coverUrl && <span>-</span>}
                </div>
                <div className="event-card-body">
                  <div className="event-card-topline">
                    <span>{event.eventType || t('events.type.privateEvent')}</span>
                    <span>{event.isPublic === 1 ? t('events.public') : t('events.private')}</span>
                  </div>
                  <h2>{event.title}</h2>
                  <p>{event.description}</p>
                  {(event.eventDate || event.locationName) && (
                    <div className="event-card-meta event-card-meta-secondary">
                      {event.eventDate && <span>{event.eventDate}</span>}
                      {event.locationName && <span>{event.locationName}</span>}
                    </div>
                  )}
                  <div className="event-card-meta">
                    <span>{event.xperienceCount || 0} {t('events.xperiences')}</span>
                    <span>{event.mediaCount || 0} {t('events.mediaItems')}</span>
                  </div>
                  <div className="event-card-actions">
                    <Button variant="ghost" onClick={() => navigate(`/events/${event.id}/edit`)}>
                      {t('events.edit')}
                    </Button>
                    <button className="event-delete-button" disabled={saving} onClick={() => handleDelete(event.id)}>
                      {t('events.delete')}
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </section>
      )}
    </>
  );

  const renderEditor = () => {
    const displayedXperiences = xperiences.filter((xperience) => !form.xperienceIds.includes(xperience.id));
    const coverPreviewUrl = assetUrl(form.coverUrl);
    const privateEventPassword = !form.isPublic ? form.password.trim() : '';
    const privateEventQRUrl = privateEventPassword
      ? `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(privateEventPassword)}`
      : '';

    return (
      <>
        <button type="button" className="events-back-button" onClick={() => navigate('/events')}>
          &lt; {t('events.backToList')}
        </button>

        <form className="event-editor event-editor-full" onSubmit={handleSubmit}>
          <div className="event-editor-header">
            <div>
              <h1 className="events-title">{eventId ? t('events.editTitle') : t('events.createTitle')}</h1>
              <p className="events-subtitle">{t('events.editorSubtitle')}</p>
            </div>
            {selectedEvent?.password && selectedEvent.isPublic !== 1 && (
              <span className="event-password-chip">{t('events.password')}: {selectedEvent.password}</span>
            )}
          </div>

          <Input
            label={t('events.field.title')}
            name="title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <div className="input-wrapper">
            <label className="input-label">{t('events.field.description')}</label>
            <textarea
              className="input"
              rows={4}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="event-form-grid">
            <div className="input-wrapper">
              <label className="input-label">{t('events.field.type')}</label>
              <select className="input" value={form.eventType} onChange={(e) => setForm((prev) => ({ ...prev, eventType: e.target.value }))}>
                <option value="wedding">{t('events.type.wedding')}</option>
                <option value="baptism">{t('events.type.baptism')}</option>
                <option value="corporate">{t('events.type.corporate')}</option>
                <option value="private_event">{t('events.type.privateEvent')}</option>
              </select>
            </div>
            <Input
              label={t('events.field.date')}
              name="eventDate"
              type="date"
              value={form.eventDate}
              onChange={(e) => setForm((prev) => ({ ...prev, eventDate: e.target.value }))}
            />
          </div>

          <Input
            label={t('events.field.locationName')}
            name="locationName"
            value={form.locationName}
            onChange={(e) => setForm((prev) => ({ ...prev, locationName: e.target.value }))}
          />

          <div className="input-wrapper">
            <label className="input-label">{t('events.field.location')}</label>
            <LocationPicker
              longitude={form.locationLongitude}
              latitude={form.locationLatitude}
              onChange={({ longitude, latitude, locationName }) => {
                setForm((prev) => ({
                  ...prev,
                  locationLongitude: longitude,
                  locationLatitude: latitude,
                  ...(locationName !== undefined ? { locationName } : {})
                }));
              }}
              mapHeight={300}
            />
          </div>

          <div className="event-poster-field">
            <div className="event-poster-header">
              <div>
                <label className="input-label">{t('events.field.poster')}</label>
                <p>{t('events.posterHelp')}</p>
              </div>
              <Button
                variant="secondary"
                type="button"
                disabled={coverUploading}
                onClick={() => coverFileInputRef.current?.click()}
              >
                {coverUploading
                  ? `${t('events.uploadingPoster')} ${coverUploadProgress}%`
                  : t('events.uploadPoster')}
              </Button>
            </div>
            <input
              ref={coverFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="event-poster-file-input"
              onChange={handleEventPosterFileChange}
            />
            <div
              className={`event-poster-preview ${coverPreviewUrl ? 'event-poster-preview-has-image' : ''}`}
              style={{ backgroundImage: coverPreviewUrl ? `url(${coverPreviewUrl})` : undefined }}
            >
              {!coverPreviewUrl && <span>{t('events.posterEmpty')}</span>}
            </div>
            <p className="event-poster-size-hint">{t('events.posterSizeHint')}</p>
          </div>

          <Input
            label={t('events.field.coverUrl')}
            name="coverUrl"
            placeholder="statics/thumbnails/example.webp"
            value={form.coverUrl}
            onChange={(e) => setForm((prev) => ({ ...prev, coverUrl: e.target.value }))}
          />

          <label className="event-private-toggle">
            <input
              type="checkbox"
              checked={!form.isPublic}
              onChange={(e) => setForm((prev) => ({ ...prev, isPublic: !e.target.checked }))}
            />
            <span>{t('events.privateToggle')}</span>
          </label>

          {!form.isPublic && (
            <Input
              label={t('events.field.password')}
              name="password"
              placeholder={t('events.placeholder.password')}
              value={form.password}
              onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
            />
          )}

          {privateEventPassword && (
            <div className="event-private-qr-panel">
              <img
                src={privateEventQRUrl}
                alt={t('events.privateQR.alt')}
                className="event-private-qr-image"
              />
              <div className="event-private-qr-content">
                <h3>{t('events.privateQR.title')}</h3>
                <p>{t('events.privateQR.description')}</p>
                <div className="event-private-qr-actions">
                  <span className="event-private-password">{privateEventPassword}</span>
                  <button
                    type="button"
                    className="event-private-qr-button event-private-qr-button-primary"
                    onClick={() => handleCopyEventPassword(privateEventPassword)}
                  >
                    {t('events.copyPassword')}
                  </button>
                  <button
                    type="button"
                    className="event-private-qr-button"
                    onClick={handleSendEventEmailQR}
                    disabled={!eventId || emailSending}
                    title={!eventId ? t('events.privateQR.saveBeforeEmail') : undefined}
                  >
                    {emailSending ? t('events.sendingEmail') : t('events.emailQR')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="event-xperience-picker">
            <div className="event-xperience-picker-header">
              <div>
                <h2>{t('events.field.xperiences')}</h2>
                <p>{t('events.xperiencePickerHelp')}</p>
              </div>
              <span>{form.xperienceIds.length} {t('events.selected')}</span>
            </div>

            <Input
              label={t('events.searchXperiences')}
              name="xperienceSearch"
              value={xperienceSearch}
              onChange={(e) => setXperienceSearch(e.target.value)}
              placeholder={t('events.searchXperiencesPlaceholder')}
            />

            {selectedXperiences.length > 0 && (
              <div className="event-xperience-selected">
                <h3>{t('events.selectedXperiences')}</h3>
                <div className="event-xperience-grid event-xperience-grid-selected">
                  {selectedXperiences.map((xperience, index) => renderXperienceCard(xperience, { sortable: true, index }))}
                </div>
              </div>
            )}

            <div className="event-xperience-results">
              <h3>{xperiencesLoading ? t('events.searchingXperiences') : t('events.availableXperiences')}</h3>
              {displayedXperiences.length > 0 ? (
                <div className="event-xperience-grid">
                  {displayedXperiences.map((xperience) => renderXperienceCard(xperience))}
                </div>
              ) : (
                <div className="events-empty events-empty-compact">
                  <p>{t('events.noXperienceResults')}</p>
                </div>
              )}
            </div>
          </div>

          <div className="event-editor-actions">
            <Button variant="primary" type="submit" disabled={saving || coverUploading}>
              {saving ? t('events.saving') : t('events.save')}
            </Button>
            <Button variant="ghost" type="button" onClick={() => navigate('/events')}>
              {t('events.cancel')}
            </Button>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="events-page">
      <Navbar />
      <div className="events-container">
        {error && <div className="events-error">{error}</div>}
        {success && <div className="events-success">{success}</div>}
        {isEditor ? renderEditor() : renderList()}
      </div>
      {renderPosterCropModal()}
    </div>
  );
};

export default EventsPage;
